import { z } from 'zod'

// Enums
export const DisclosureRequesterType = z.enum(['lea', 'cert', 'ip_enforcement', 'other'])
export type DisclosureRequesterType = z.infer<typeof DisclosureRequesterType>

export const DisclosureStatus = z.enum([
  'pending',
  'in_progress',
  'completed',
  'rejected',
  'forwarded_to_inwx'
])
export type DisclosureStatus = z.infer<typeof DisclosureStatus>

export const AbuseCaseCategory = z.enum([
  'spam',
  'phishing',
  'malware',
  'fake_data',
  'trademark',
  'other'
])
export type AbuseCaseCategory = z.infer<typeof AbuseCaseCategory>

export const AbuseCaseStatus = z.enum(['open', 'investigating', 'resolved', 'rejected'])
export type AbuseCaseStatus = z.infer<typeof AbuseCaseStatus>

// Compliance Dashboard
export const ComplianceDashboard = z.object({
  pending_verification_handles: z.number().int(),
  expired_verification_handles: z.number().int(),
  suspended_domains: z.number().int(),
  pending_disclosure_requests: z.number().int(),
  open_abuse_cases: z.number().int()
})
export type ComplianceDashboard = z.infer<typeof ComplianceDashboard>

// Disclosure Request
export const DisclosureRequest = z.object({
  id: z.number().int(),
  created: z.string(),
  requester_type: DisclosureRequesterType,
  requester_name: z.string(),
  requester_email: z.string().email(),
  requester_org: z.string().nullable().optional(),
  request_reference: z.string().nullable().optional(),
  domain_name: z.string().nullable().optional(),
  account_id: z.number().int().nullable().optional(),
  request_text: z.string().optional(),
  status: DisclosureStatus,
  deadline: z.string(),
  responded_at: z.string().nullable().optional(),
  responded_by: z.string().nullable().optional(),
  response_text: z.string().nullable().optional(),
  is_overdue: z.boolean()
})
export type DisclosureRequest = z.infer<typeof DisclosureRequest>

export const DisclosureRequestCreate = z.object({
  requester_type: DisclosureRequesterType,
  requester_name: z.string().min(1),
  requester_email: z.string().email(),
  requester_org: z.string().optional(),
  request_reference: z.string().optional(),
  domain_name: z.string().optional(),
  account_id: z.number().int().optional(),
  request_text: z.string().min(1)
})
export type DisclosureRequestCreate = z.infer<typeof DisclosureRequestCreate>

export const DisclosureRequestUpdate = z.object({
  status: DisclosureStatus,
  response_text: z.string().optional()
})
export type DisclosureRequestUpdate = z.infer<typeof DisclosureRequestUpdate>

// Abuse Case
export const AbuseCase = z.object({
  id: z.number().int(),
  created: z.string(),
  reporter_email: z.string().email(),
  reporter_name: z.string().nullable().optional(),
  category: AbuseCaseCategory,
  domain_name: z.string().nullable().optional(),
  description: z.string(),
  status: AbuseCaseStatus,
  resolution_text: z.string().nullable().optional(),
  resolved_at: z.string().nullable().optional(),
  resolved_by: z.string().nullable().optional()
})
export type AbuseCase = z.infer<typeof AbuseCase>

export const AbuseCaseUpdate = z.object({
  status: AbuseCaseStatus,
  resolution_text: z.string().optional()
})
export type AbuseCaseUpdate = z.infer<typeof AbuseCaseUpdate>

// Suspended Domain
export const SuspendedDomain = z.object({
  id: z.number().int(),
  domain_name: z.string(),
  suspended_at: z.string(),
  reason: z.string(),
  suspended_by: z.string().nullable().optional(),
  owner_email: z.string().email().nullable().optional()
})
export type SuspendedDomain = z.infer<typeof SuspendedDomain>

export const SuspendDomainRequest = z.object({
  reason: z.string().min(1)
})
export type SuspendDomainRequest = z.infer<typeof SuspendDomainRequest>

// Handle Pending Verification
export const PendingVerificationHandle = z.object({
  id: z.number().int(),
  external_id: z.number().int(),
  external_name: z.string().nullable().optional(),
  type: z.enum(['person', 'org', 'role']),
  email_verified: z.boolean(),
  phone_verified: z.boolean(),
  verification_deadline: z.string(),
  account_id: z.number().int(),
  account_email: z.string()
})
export type PendingVerificationHandle = z.infer<typeof PendingVerificationHandle>
